import { useRef } from 'react'
import { toast } from 'react-hot-toast'
import { useParams } from 'react-router'

import {
  FvButton,
  FvLinkButton,
  Icon,
  OrDivider,
  useFvNavigate,
} from '@fv/client-components'
import { addressFuncs } from '@fv/client-core'
import type { Workflow } from '@fv/models'

import { FooterBar } from '@/components/FooterBar'
import InfoBox from '@/components/InfoBox'
import CheckboxField from '@/components/inputs/CheckboxField'
import { InputGroup } from '@/components/inputs/InputGroup'
import type { FormSectionProps } from '@/components/inputs/types'
import { Loading } from '@/components/Loading'
import { useQuoteSettings } from '@/hooks/settings'
import { useCancelLoad } from '@/hooks/shipments'
import { routes } from '@/routes'
import { locationToFormStop } from '@/utils/locationFuncs'
import { defaultStopDate } from '@/utils/shipmentFuncs'

import { useHasPermission } from '../auth/hooks'
import { requireRateLocation } from '../book/bookFuncs'
import { FedExHomeDeliveryQuoting } from '../book/FedExHomeDelivery'
import {
  LoadItemsForm,
  type LoadItemsFormRef,
} from '../commodities/load-items/LoadItemsFormProvider'
import { defaultLoadHandlingUnit } from '../commodities/load-items/loadItemHelpers'
import type { LoadHandlingUnitFormModel } from '../commodities/load-items/types'
import { useSaveCustomerRoutedDraft } from '../customer-routed/hooks'
import { useCreateQuoteRequest } from '../quote-request/useCreateQuoteRequest'
import { useUpdateQuoteRequest } from '../quote-request/useUpdateQuoteRequest'
import { AutofillPreviousShipment } from '../shipment-form/AutofillPreviousShipment'
import {
  defaultDestination,
  defaultOrigin,
} from '../shipment-location-form/types'
import { QuoteFormLocations } from './QuoteFormLocations'
import { useQuotePageCtx, useQuotePageWorkflow } from './QuotePageProvider'
import type { QuoteRequestMode } from './quoteFuncs'

type SettingsPanelProps = FormSectionProps & {
  workflow: Workflow
  mode: QuoteRequestMode
  onItemsChange: (items: LoadHandlingUnitFormModel[]) => void
}

const SettingsPanel = ({
  disabled,
  workflow,
  mode,
  onItemsChange,
}: SettingsPanelProps) => {
  const actions = useQuotePageCtx(s => s.actions)
  const selectedTemplateId = useQuotePageCtx(s => s.selectedTemplate?._id)

  return (
    <div className="bg-fv-blue-50 border-fv-blue-300 -mx-6 -mt-10 mb-10 flex items-center border-b border-dashed px-6 py-4">
      <ul className="mb-0 flex items-center">
        <li className="whitespace-nowrap border-0 py-0">
          <FvLinkButton
            disabled={disabled}
            icon="history"
            to={routes.recentQuotes(workflow)}
          >
            <span>Recent quotes</span>
          </FvLinkButton>
        </li>
        <li className="ml-4 mr-4 whitespace-nowrap border-0 border-fv-gray border-l border-r py-0 pl-4 pr-4">
          <FvLinkButton
            disabled={disabled}
            icon="star-sharp"
            to={routes.savedQuotes(workflow)}
            transform="up-1"
          >
            <span>Saved quotes</span>
          </FvLinkButton>
        </li>
      </ul>
      {mode !== 'edit' && mode !== 'copy' && (
        <AutofillPreviousShipment
          workflow={workflow}
          selectedTemplateId={selectedTemplateId}
          handlers={{
            origin: (loc, enabled) =>
              actions.setLocationData(
                'origin',
                enabled && loc
                  ? {
                      ...locationToFormStop(loc, workflow),
                      stopDate: defaultStopDate('origin'),
                      refNum: '',
                    }
                  : defaultOrigin(),
              ),
            destination: (loc, enabled) =>
              actions.setLocationData(
                'destination',
                enabled && loc
                  ? locationToFormStop({ ...loc, stopDate: null }, workflow)
                  : defaultDestination(),
              ),
            product: (items, enabled) =>
              onItemsChange(
                enabled ? items : [defaultLoadHandlingUnit(workflow)],
              ),
          }}
        />
      )}
    </div>
  )
}

export type QuotePageFormParams = {
  loadId?: string
  mode?: QuoteRequestMode
}

export const QuotePageForm = () => {
  const { loadId, mode } = useParams<QuotePageFormParams>()
  const itemsFormRef = useRef<LoadItemsFormRef>(null)
  const actions = useQuotePageCtx(s => s.actions)
  const workflow = useQuotePageWorkflow()
  const items = useQuotePageCtx(s => s.items)
  const itemsFormKey = useQuotePageCtx(s => s.itemsFormKey)
  const isEditing = loadId && mode === 'edit'
  const isFreightCollect = useQuotePageCtx(s => s.load.isFreightCollect)
  const protectFromFreezing = useQuotePageCtx(s => s.load.protectFromFreezing)
  const singleShipment = useQuotePageCtx(s => s.load.singleShipment)
  const rateLocationType = useQuotePageCtx(s => s.load.rateLocationType)
  const locations = useQuotePageCtx(s => s.locations)
  const canBookLoads = useHasPermission('load-booker')
  const navigate = useFvNavigate()
  const requestQuotes = useCreateQuoteRequest()
  const cancelLoad = useCancelLoad(loadId, false)
  const quoteRequestId = useQuotePageCtx(s => s.load.quoteRequestId)
  const updateQuoteRequest = useUpdateQuoteRequest(quoteRequestId)
  const saveCustomerRoutedDraft = useSaveCustomerRoutedDraft()
  const quoteSettings = useQuoteSettings(workflow)
  const isLoading =
    quoteSettings.isLoading ||
    requestQuotes.isPending ||
    requestQuotes.isSuccess ||
    updateQuoteRequest.isPending ||
    updateQuoteRequest.isSuccess ||
    cancelLoad.isPending ||
    cancelLoad.isSuccess

  const showProtectFromFreezing = workflow === 'ltl'
  const showSingleShipment = workflow === 'ltl'
  const showFreightCollect = canBookLoads
  const showRateLocationSelect = requireRateLocation(
    locations,
    isFreightCollect,
  )
  const showAccessorialsBox =
    showProtectFromFreezing ||
    showSingleShipment ||
    showFreightCollect ||
    showRateLocationSelect

  const parentClasses =
    'py-10 px-6 bg-white flex min-h-0 flex-1 flex-col overflow-auto b1200:pb-28'

  async function submitForm() {
    if (isLoading) return
    if (
      !itemsFormRef.current?.validateForm() ||
      !itemsFormRef.current?.validateItems(workflow, 'quoting')
    ) {
      toast.error('Please fill in all required fields')
      return
    }

    const quoteRequest = actions.buildRequest()
    if (!quoteRequest) return

    if (isFreightCollect) {
      if (isEditing) {
        // The only way editing & isFreightCollect is true is if they created the load as non-freight-collect originally
        // so we cancel the original load
        try {
          await cancelLoad.mutateAsync(false)
        } catch {
          // if we fail to cancel the original load for any reason - the worst that could happen is they have
          // a duplicate entry in their in process page.  This doesn't seem worth preventing them from creating
          // the CR shipment
        }
      }
      saveCustomerRoutedDraft(quoteRequest)
      return navigate(routes.customerRouted())
    }

    if (isEditing) {
      updateQuoteRequest.mutate(quoteRequest)
    } else {
      requestQuotes.mutate(quoteRequest, {
        onSuccess: loads => {
          navigate(routes.rates(loads[0]))
        },
      })
    }
  }

  if (
    workflow === 'parcel' &&
    !quoteSettings.data?.apiEnabledCarriers?.length
  ) {
    return (
      <div className={parentClasses}>
        <div className="b900:flex-col flex gap-x-6 b1500:gap-x-4 items-stretch b1350:gap-x-6">
          <InfoBox>
            For a more detailed look at our{' '}
            <a
              className="text-fv-blue"
              href="https://blog.freightview.com/parcel-in-freightview"
              target="blank"
            >
              parcel product
            </a>{' '}
            and how to add it to your account please visit our{' '}
            <a
              className="text-fv-blue"
              href="https://blog.freightview.com/parcel-in-freightview"
              target="blank"
            >
              blog article
            </a>
            . If you have any more questions please contact our Support Team at{' '}
            <a href="mailto:support@freightview.com" className="text-fv-blue">
              support@freightview.com
            </a>
          </InfoBox>
        </div>
      </div>
    )
  }

  return (
    <>
      {isLoading ? (
        <div className={parentClasses}>
          <div className="b900:flex-col flex gap-x-6 b1500:gap-x-4 items-stretch b1350:gap-x-6">
            <Loading>Loading Shipment...</Loading>
          </div>
        </div>
      ) : (
        <div className={parentClasses}>
          <SettingsPanel
            disabled={isLoading}
            mode={mode}
            workflow={workflow}
            onItemsChange={items => {
              actions.setItems(items)
              itemsFormRef.current?.setItems(items)
            }}
          />
          <div className="b900:flex-col flex gap-x-6 b1500:gap-x-4 items-stretch b1350:gap-x-6">
            <QuoteFormLocations disabled={isLoading} onSubmit={submitForm} />

            <div className="basis-1/2 bg-gray-50 pt-7 px-4 pb-6 relative border border-fv-gray">
              <LoadItemsForm
                key={itemsFormKey}
                onSubmit={submitForm}
                ref={itemsFormRef}
                type="paged"
                workflow={workflow}
                step="quoting"
                onChange={actions.setItems}
                initialItems={items}
              />
            </div>
          </div>
          {showAccessorialsBox && (
            <div className="mt-4 border bg-fv-blue-50 border-fv-blue-300 px-4 py-3 relative">
              <div className="flex gap-x-4 items-center">
                <div className="flex gap-x-2 items-center">
                  <span>Accessorials / billing</span>
                  <Icon icon="arrow-right" className="text-fv-gray-dark" />
                </div>
                {showProtectFromFreezing && (
                  <CheckboxField
                    alternate
                    checked={protectFromFreezing}
                    disabled={isLoading}
                    label="Protect from freezing"
                    name="protectFromFreezing"
                    onChange={actions.handleLoadChange}
                  />
                )}
                {showSingleShipment && (
                  <CheckboxField
                    alternate
                    checked={singleShipment}
                    disabled={isLoading}
                    label="Single shipment"
                    name="singleShipment"
                    onChange={actions.handleLoadChange}
                  />
                )}
                {showFreightCollect && (
                  <CheckboxField
                    alternate
                    checked={isFreightCollect}
                    disabled={isLoading}
                    label="Customer routed shipment (bill recipient / 3rd party)"
                    name="isFreightCollect"
                    onChange={actions.handleLoadChange}
                  />
                )}
              </div>
              {showRateLocationSelect && (
                <InputGroup
                  className="basis-full border-t mt-3 border-fv-blue-300 pt-3 border-dashed"
                  inputProps={{
                    disabled: isLoading,
                    name: 'rateLocationType',
                    onChange: actions.handleLoadChange,
                    options: locations.map(x => ({
                      text: addressFuncs.streetAddress(x),
                      value: x.sequence === 0 ? 'origin' : 'destination',
                    })),
                    value: rateLocationType,
                  }}
                  inputType="select"
                  label="Which account would you like to use to rate this shipment?"
                />
              )}
            </div>
          )}

          {workflow === 'parcel' && <FedExHomeDeliveryQuoting />}
        </div>
      )}

      <FooterBar jc className="shrink-0">
        {loadId && mode && (
          <>
            <FvButton icon="times" onClick={() => navigate(-1)} theme="default">
              Cancel
            </FvButton>
            <OrDivider className="mx-3" />
          </>
        )}

        <FvButton
          fwd
          icon={isLoading ? 'spinner' : 'dollar-sign'}
          onClick={submitForm}
          theme="primary"
        >
          {isFreightCollect
            ? 'Book shipment'
            : isEditing
              ? 'Save changes'
              : 'Show my rates'}
        </FvButton>
      </FooterBar>
    </>
  )
}
